import * as React from "react";
import { useEventContext } from "@/contexts/EventContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useAuthContext } from "@/contexts/AuthContext";
import {
  eventToWedding,
  weddingDetailsStore,
  weddingInputToCreateEventInput,
  weddingPatchToUpdateEventInput,
  splitCoupleNames,
} from "@/lib/adapters/wedding-event-adapter";
import { isWeddingEvent } from "@/types/domain/event";
import type { Wedding, WeddingInput } from "@/types/wedding";

interface WeddingContextValue {
  weddings: Wedding[];
  activeWedding: Wedding | null;
  isLoading: boolean;
  setActiveWeddingId: (id: string) => void;
  createWedding: (input: WeddingInput) => Promise<void>;
  updateWedding: (id: string, patch: Partial<WeddingInput>) => Promise<void>;
  suspendWedding: (id: string) => Promise<void>;
  resumeWedding: (id: string) => Promise<void>;
  deleteWedding: (id: string) => Promise<void>;
  restoreWedding: (id: string) => Promise<void>;
}

const WeddingContext = React.createContext<WeddingContextValue | undefined>(undefined);

/**
 * COMPATIBILITY LAYER — WeddingContext is now a thin read/write adapter
 * over the generic EventContext, not an independent data store. There is
 * exactly one active-selection source of truth (EventContext); this
 * context only ever filters events down to the "wedding" EventType and
 * translates the Wedding shape at the edges, via
 * src/lib/adapters/wedding-event-adapter.ts.
 *
 * Every existing consumer — WeddingsPage, WeddingDetailPage,
 * GuestContext, BudgetContext, ChecklistContext, the dashboard — keeps
 * calling `useWedding()` exactly as before. None of them had to change.
 */
export function WeddingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const { activeOrganization } = useOrganizationContext();
  const { events, activeEvent, isLoading, setActiveEventId, createEvent, updateEvent } = useEventContext();

  const weddings = React.useMemo<Wedding[]>(
    () => events.filter(isWeddingEvent).map(eventToWedding),
    [events]
  );

  const activeWedding = React.useMemo<Wedding | null>(
    () => (activeEvent && !activeEvent.deletedAt && isWeddingEvent(activeEvent) ? eventToWedding(activeEvent) : null),
    [activeEvent]
  );

  const createWedding = React.useCallback(
    async (input: WeddingInput) => {
      if (!activeOrganization || !user) throw new Error("Select an organization and sign in before creating a wedding.");
      if (!input.coupleNames.trim() || !input.weddingDate) throw new Error("Couple names and wedding date are required.");
      const eventInput = weddingInputToCreateEventInput(input, activeOrganization.id, user.id);
      const created = await createEvent(eventInput);
      weddingDetailsStore.upsert(created.id, splitCoupleNames(input.coupleNames));
    },
    [createEvent, activeOrganization, user]
  );

  const updateWedding = React.useCallback(
    async (id: string, patch: Partial<WeddingInput>) => {
      const eventPatch = weddingPatchToUpdateEventInput(patch);
      if (Object.keys(eventPatch).length > 0) {
        await updateEvent(id, eventPatch);
      }
      if (patch.coupleNames !== undefined) {
        weddingDetailsStore.upsert(id, splitCoupleNames(patch.coupleNames));
      }
    },
    [updateEvent]
  );

  const suspendWedding = React.useCallback(async (id: string) => {
    await updateEvent(id, { suspendedAt: new Date().toISOString() });
  }, [updateEvent]);

  const resumeWedding = React.useCallback(async (id: string) => {
    await updateEvent(id, { suspendedAt: null });
  }, [updateEvent]);

  const deleteWedding = React.useCallback(async (id: string) => {
    const deletedAt = new Date();
    const purgeAfter = new Date(deletedAt);
    purgeAfter.setDate(purgeAfter.getDate() + 30);
    await updateEvent(id, {
      suspendedAt: null,
      deletedAt: deletedAt.toISOString(),
      purgeAfter: purgeAfter.toISOString(),
    });
  }, [updateEvent]);

  const restoreWedding = React.useCallback(async (id: string) => {
    await updateEvent(id, { deletedAt: null, purgeAfter: null });
  }, [updateEvent]);

  const value = React.useMemo<WeddingContextValue>(
    () => ({
      weddings,
      activeWedding,
      isLoading,
      setActiveWeddingId: setActiveEventId,
      createWedding,
      updateWedding,
      suspendWedding,
      resumeWedding,
      deleteWedding,
      restoreWedding,
    }),
    [weddings, activeWedding, isLoading, setActiveEventId, createWedding, updateWedding, suspendWedding, resumeWedding, deleteWedding, restoreWedding]
  );

  return <WeddingContext.Provider value={value}>{children}</WeddingContext.Provider>;
}

export function useWeddingContext(): WeddingContextValue {
  const ctx = React.useContext(WeddingContext);
  if (!ctx) throw new Error("useWeddingContext must be used within a WeddingProvider");
  return ctx;
}
